import React from 'react';
import PropTypes, { any } from 'prop-types';
import moment from 'moment-timezone';

import { CheckBox, Input, Select, DateTimePicker } from '../../Form';
import { required } from '../../../utils/validation';
import useQualifyingDraw from './useQualifyingDraw';
import { getFeedOutRoundOptions, EventTypes } from '../../../utils/events';
import { allowedMatchTimes } from '../../../utils/allottedTimeConstants';

import styles from './eventDrawCreateEditPopup.scss';
import { sportTypeIds } from '../../../utils/eventConstants';

const QualifyingDrawSection = (props) => {
    const {
        sportTypeId,
        eventTypeId,
        formApi,
        field,
        qualifyingDrawType,
        className,
        isInEditMode,
        forceDoubles,
        isVerifiedEvent,
        eventAllottedMatchTimes,
        isInEditConfig,
        eventSchedule,
    } = props;

    const { values } = formApi;

    const {
        isAllottedTimeFlagEnabled,
        selectableDrawSizes,
        qualifyingDrawEliminationTypes,
        dateToDisplay,
        onQualifyingDrawEliminationChange,
        onDrawSizeChange,
        onChangeDrawEnabled,
        getScoringFormats,
        onChangeScoringType,
        handleDateChange,
    } = useQualifyingDraw({
        sportTypeId,
        eventTypeId,
        field,
        formApi,
        forceDoubles,
        isVerifiedEvent,
        eventAllottedMatchTimes,
        timeZone: eventSchedule?.timeZone ?? null,
    });

    const isEnabled = values[field]?.enabled;
    const drawSize = values[field]?.drawSize;
    const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(eventSchedule?.timeZone?.value);
    const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule?.timeZone?.value);

    return (
        <div className={`${className}`}>
            <CheckBox
                id={`${field}-enabled`}
                formGroupCssClass={`mb10 ${styles.dummy}`}
                customLabel={`Include ${qualifyingDrawType} draw`}
                field={[field, 'enabled']}
                onChange={onChangeDrawEnabled}
                disabled={isInEditMode}
            />
            {isEnabled && (
                <>
                    <Select
                        id={`${field}-eliminationTypeId`}
                        field={[field, 'eliminationTypeId']}
                        label="Draw type"
                        options={qualifyingDrawEliminationTypes}
                        validate={required}
                        onChange={onQualifyingDrawEliminationChange}
                        disabled={isInEditMode}
                        themeV3
                    />
                    {!isInEditConfig && (
                        <>
                            <Input
                                id={`${field}-name`}
                                label="Draw name"
                                field={[field, 'name']}
                                placeholder="Ex. Men's Singles"
                                validate={required}
                                themeV3
                            />
                            <DateTimePicker
                                id={`${field}-startDateTimeUtc`}
                                field={[field, 'startDateTimeUtc']}
                                dateLabel="Start date"
                                timeLabel="Start time"
                                selected={dateToDisplay}
                                minDate={eventStartDate}
                                maxDate={eventEndDate}
                                handleDateChange={handleDateChange('startDateTimeUtc', formApi)}
                                themeV3
                                defaultDate={eventStartDate}
                            />
                            <Select
                                id={`${field}-drawsize`}
                                label="Draw size"
                                field={[field, 'drawSize']}
                                options={selectableDrawSizes}
                                validate={required}
                                onChange={onDrawSizeChange}
                                disabled={isInEditMode}
                                themeV3
                            />
                            <Select
                                id={`${field}-feedoutround`}
                                label="Feed-in-Qualifier Cutoff Round"
                                field={[field, 'feedOutRound']}
                                options={getFeedOutRoundOptions(parseInt(drawSize), parseInt(values.formatTypeId))}
                                themeV3
                                emptyOption="Select last round"
                                hoverText="Subsequent rounds will not be shown on the event profile. Winners of the last round selected in this draw can be manually moved to the first round of the next draw."
                                disabled={isInEditMode}
                                validate={required}
                            />
                        </>
                    )}
                    {!isAllottedTimeFlagEnabled && (
                        <Select
                            id={`${field}-scoringTypeId`}
                            field={[field, 'scoringTypeId']}
                            label="Default scoring format"
                            options={getScoringFormats(values)}
                            themeV3
                        />
                    )}
                    {isAllottedTimeFlagEnabled && (
                        <div className="d-flex align-items-start">
                            <div className="flex-1">
                                <Select
                                    id={`${field}-scoringTypeId`}
                                    field={[field, 'scoringTypeId']}
                                    label="Default scoring format"
                                    options={getScoringFormats(values)}
                                    themeV3
                                    formGroupCssClass={`mb0`}
                                    validate={required}
                                    onChange={onChangeScoringType}
                                />
                            </div>
                            <div className="flex-1 ml16">
                                <Select
                                    id={`${field}-allottedMatchTime`}
                                    field={[field, 'allottedMatchTime']}
                                    label="Default allotted time"
                                    options={allowedMatchTimes}
                                    themeV3
                                    formGroupCssClass={`mb0`}
                                    validate={required}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

QualifyingDrawSection.propTypes = {
    sportTypeId: PropTypes.number,
    eventTypeId: PropTypes.number,
    formApi: PropTypes.objectOf(any).isRequired,
    field: PropTypes.string.isRequired,
    qualifyingDrawType: PropTypes.string.isRequired,
    className: PropTypes.string,
    isInEditMode: PropTypes.bool,
    forceDoubles: PropTypes.bool,
    isVerifiedEvent: PropTypes.bool,
    eventAllottedMatchTimes: PropTypes.arrayOf(any),
    isInEditConfig: PropTypes.bool,
    eventSchedule: PropTypes.objectOf(any).isRequired,
};

QualifyingDrawSection.defaultProps = {
    sportTypeId: sportTypeIds.TENNIS,
    eventTypeId: EventTypes.TOURNAMENT,
    className: '',
    isInEditMode: false,
    forceDoubles: false,
    isVerifiedEvent: false,
    eventAllottedMatchTimes: [],
    isInEditConfig: false,
};

export default QualifyingDrawSection;
